/**
 * The travel map: an interactive, full-screen version of the debug political
 * map (`src/debug/world-map.ts`) that lets a player click a settlement -- on
 * the canvas or in the sidebar list -- to teleport there.
 *
 * Reuses `WorldMapField` wholesale rather than re-deriving city positions:
 * the political layer already has the one list of settlements worth
 * travelling to (`citiesInBox` in `polity.ts`), and duplicating that query
 * here would be a second source of truth for "where are the settlements",
 * exactly what `REAL_TERRAIN`/`REAL_CLIMATE` in `world-map.ts` already
 * guards against for terrain. Villages are deliberately NOT listed: they have
 * no global registry (they are generated on demand per road, per
 * `world-map.ts`'s own header), so only political cities -- capitals and
 * city-states -- are reachable by teleport.
 *
 * Two exported pure functions (`settlementsSortedByDistance`,
 * `pickCityNear`, `worldToFieldPixel`) hold all the logic that has an
 * observable right answer; `TravelMap` itself is a thin DOM shell around
 * them, in the same "pure sampler, thin wrapper" split `world-map.ts`
 * documents at length.
 */

import type { CitySite } from '../world/polity';
import { REAL_CLIMATE, REAL_TERRAIN, WorldMapField } from '../debug/world-map';

/**
 * Pixels along one edge and world metres per pixel of the travel view.
 * `CITY_CELL` (`polity.ts`) is 8,192 m, so this ~135 km span comfortably
 * covers several cells in every direction -- enough to see distant nations
 * worth flying or walking toward, not just the player's own.
 */
const SIZE = 512;
const METRES_PER_PIXEL = 264;
/** Click tolerance around a city's dot, in FIELD pixels (not CSS pixels). */
const CLICK_TOLERANCE_PX = 9;
/** One incremental build step's budget when the map first opens. */
const OPEN_BUDGET_MS = 40;
/** Budget per subsequent frame while still building. Small: this overlay is not the main render loop's hot path, but it still shares the frame. */
const STEP_BUDGET_MS = 4;

export interface TravelTarget {
  readonly city: CitySite;
  readonly name: string;
  readonly distanceMetres: number;
}

/** Every city the field currently has in view, nearest first. */
export function settlementsSortedByDistance(
  field: WorldMapField,
  fromX: number,
  fromZ: number,
): TravelTarget[] {
  const targets = field.citiesInView().map((city) => {
    const dx = city.x - fromX;
    const dz = city.z - fromZ;
    return { city, name: field.nameOfCity(city), distanceMetres: Math.hypot(dx, dz) };
  });
  targets.sort((a, b) => a.distanceMetres - b.distanceMetres);
  return targets;
}

/**
 * The city whose dot is nearest a clicked point, within `toleranceFieldPx`.
 * `null` if the click missed every dot -- a click on empty terrain is not a
 * teleport, it is a no-op.
 */
export function pickCityNear(
  field: WorldMapField,
  fieldPx: number,
  fieldPz: number,
  toleranceFieldPx: number,
): CitySite | null {
  let best: CitySite | null = null;
  let bestDistSq = toleranceFieldPx * toleranceFieldPx;
  for (const city of field.citiesInView()) {
    const { px, pz } = field.screenPositionOf(city);
    const dx = px - fieldPx;
    const dz = pz - fieldPz;
    const distSq = dx * dx + dz * dz;
    if (distSq <= bestDistSq) {
      bestDistSq = distSq;
      best = city;
    }
  }
  return best;
}

/**
 * Where an arbitrary world point falls in the field's current view, in field
 * pixels (may be off-canvas). The same formula `WorldMapField.screenPositionOf`
 * uses internally for a `CitySite`, exposed here for points that are not one
 * -- the "you are here" marker is the only caller.
 */
export function worldToFieldPixel(
  field: WorldMapField,
  x: number,
  z: number,
): { px: number; pz: number } {
  const mpp = field.metresPerPixel;
  const originX = field.centerX - (field.width / 2) * mpp;
  const originZ = field.centerZ - (field.height / 2) * mpp;
  return { px: (x - originX) / mpp, pz: (z - originZ) / mpp };
}

export interface TravelMapOptions {
  /** Called with the world (x, z) of a chosen settlement. Never called with a `y`; the caller owns ground placement. */
  onTeleport: (x: number, z: number) => void;
}

/**
 * Owns a full-screen overlay `<div>`: a canvas half of the political map at a
 * wider scale, and a sidebar list of the same settlements by name and
 * distance -- the list exists because a dot a few pixels across is a poor
 * click target once a view spans 135 km, while a name in a list never misses.
 *
 * Movement is expected to be frozen by the caller while this is open (`App`
 * gates `rig`/`player` updates on `travelMap.open`) -- the camera can't move
 * mid-build, so the field is built once around the position passed to
 * `openAt` and never re-centred until the next open.
 */
export class TravelMap {
  readonly element: HTMLDivElement;

  private readonly field: WorldMapField;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly imageData: ImageData;
  private readonly list: HTMLDivElement;
  private readonly onTeleport: (x: number, z: number) => void;

  private isOpen = false;
  private cameraX = 0;
  private cameraZ = 0;

  constructor(worldSeed: number, options: TravelMapOptions) {
    this.onTeleport = options.onTeleport;
    this.field = new WorldMapField(
      { terrain: REAL_TERRAIN, climate: REAL_CLIMATE, worldSeed },
      { size: SIZE, metresPerPixel: METRES_PER_PIXEL },
    );

    this.element = document.createElement('div');
    this.element.className = 'travel-map';
    Object.assign(this.element.style, {
      position: 'fixed',
      inset: '0',
      // lil-gui's own panel (`DebugPanel`) sits at z-index 1001. This has to
      // clear that or the panel intercepts clicks meant for the list/canvas
      // underneath it whenever the two overlap on screen -- confirmed live:
      // a click on a settlement row silently did nothing until this was
      // raised above the panel's z-index.
      zIndex: '2000',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.78)',
      font: '13px ui-monospace, Menlo, Consolas, monospace',
      color: '#d8e4ea',
    });

    const hint = document.createElement('div');
    hint.textContent = 'Click a settlement -- on the map or in the list -- to travel there. Esc to close.';
    Object.assign(hint.style, {
      position: 'absolute',
      top: '18px',
      width: '100%',
      textAlign: 'center',
      pointerEvents: 'none',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, { display: 'flex', gap: '16px', alignItems: 'flex-start' });

    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    Object.assign(this.canvas.style, {
      width: '72vmin',
      height: '72vmin',
      maxWidth: '72vw',
      maxHeight: '72vh',
      imageRendering: 'pixelated',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      cursor: 'crosshair',
    });
    this.canvas.addEventListener('click', this.onCanvasClick);

    const ctx = this.canvas.getContext('2d');
    if (ctx === null) throw new Error('TravelMap: 2D canvas context unavailable');
    this.ctx = ctx;
    this.imageData = ctx.createImageData(SIZE, SIZE);

    this.list = document.createElement('div');
    Object.assign(this.list.style, {
      width: '240px',
      maxHeight: '72vmin',
      overflowY: 'auto',
    });

    panel.append(this.canvas, this.list);
    this.element.append(hint, panel);
    document.addEventListener('keydown', this.onKeyDown);
  }

  get open(): boolean {
    return this.isOpen;
  }

  /** Open centred on the given world position, or close if already open. */
  toggle(cameraX: number, cameraZ: number): void {
    if (this.isOpen) this.close();
    else this.openAt(cameraX, cameraZ);
  }

  openAt(cameraX: number, cameraZ: number): void {
    this.isOpen = true;
    this.cameraX = cameraX;
    this.cameraZ = cameraZ;
    this.element.style.display = 'flex';
    this.field.recenter(cameraX, cameraZ);
    // The city LIST needs no pixel build to be correct (`citiesInView` reads
    // straight off `citiesInBox`), so it is usable the instant the map opens
    // even though the canvas is still painting.
    this.renderList();
    this.step(OPEN_BUDGET_MS);
  }

  close(): void {
    this.isOpen = false;
    this.element.style.display = 'none';
  }

  /** Advance the incremental build. No-op once closed or fully painted. */
  update(budgetMs = STEP_BUDGET_MS): void {
    if (!this.isOpen || this.field.done) return;
    this.step(budgetMs);
  }

  dispose(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    this.canvas.removeEventListener('click', this.onCanvasClick);
    this.element.remove();
  }

  private step(budgetMs: number): void {
    this.field.step(budgetMs);
    this.imageData.data.set(this.field.pixels);
    this.ctx.putImageData(this.imageData, 0, 0);
    this.drawLabels();
    this.drawPlayerMarker();
  }

  private drawLabels(): void {
    this.ctx.font = '11px ui-monospace, Menlo, Consolas, monospace';
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    this.ctx.textBaseline = 'top';
    for (const city of this.field.citiesInView()) {
      const { px, pz } = this.field.screenPositionOf(city);
      if (px < -60 || px > this.field.width + 60 || pz < -14 || pz > this.field.height + 14) continue;
      this.ctx.fillText(this.field.nameOfCity(city), px + 4, pz + 3);
    }
  }

  private drawPlayerMarker(): void {
    const { px, pz } = worldToFieldPixel(this.field, this.cameraX, this.cameraZ);
    if (px < 0 || px > this.field.width || pz < 0 || pz > this.field.height) return;
    this.ctx.fillStyle = '#ffcc33';
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    this.ctx.lineWidth = 1.5;
    this.ctx.beginPath();
    this.ctx.arc(px, pz, 4.5, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.stroke();
  }

  private renderList(): void {
    const targets = settlementsSortedByDistance(this.field, this.cameraX, this.cameraZ);
    this.list.replaceChildren();
    if (targets.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No settlements in view.';
      empty.style.opacity = '0.7';
      this.list.append(empty);
      return;
    }
    for (const target of targets) {
      const row = document.createElement('div');
      row.textContent = `${target.name} — ${(target.distanceMetres / 1000).toFixed(1)} km`;
      Object.assign(row.style, {
        padding: '4px 6px',
        cursor: 'pointer',
        borderRadius: '3px',
      });
      row.addEventListener('mouseenter', () => {
        row.style.background = 'rgba(255, 255, 255, 0.14)';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = '';
      });
      row.addEventListener('click', () => this.teleportToCity(target.city));
      this.list.append(row);
    }
  }

  private teleportToCity(city: CitySite): void {
    this.onTeleport(city.x, city.z);
    this.close();
  }

  private readonly onCanvasClick = (event: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const fieldPx = ((event.clientX - rect.left) / rect.width) * this.field.width;
    const fieldPz = ((event.clientY - rect.top) / rect.height) * this.field.height;
    const city = pickCityNear(this.field, fieldPx, fieldPz, CLICK_TOLERANCE_PX);
    if (city !== null) this.teleportToCity(city);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.isOpen) return;
    if (event.code === 'Escape') this.close();
  };
}
